import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Button, Form, Modal, Dropdown, Checkbox } from 'semantic-ui-react';
import { Input } from '../../lib/custom-ui';

import { useForm } from '../../hooks';

import styles from './DeviceEditStep.module.scss';

const DeviceEditStep = React.memo(({ devices, channels, models, device, onUpdate, onClose }) => {
  const [t] = useTranslation();

  const channelsOptions = [];
  channels.forEach((item) => {
    channelsOptions.push({ key: item.id, value: item.id, text: item.name });
  });

  const modelsOptions = [];
  models.forEach((item) => {
    modelsOptions.push({ key: item.id, value: item.id, text: item.name });
  });

  const devicesOptions = [];
  devices.forEach((item) => {
    devicesOptions.push({ key: item.id, value: item.id, text: item.name });
  });

  // check all deviceIds existed
  const { deviceIds } = device;
  const devicesIds = devices.map((d) => d.id);
  const allDeviceIdsExisted = deviceIds.every((val) => devicesIds.includes(val));

  const [nameError, setNameError] = useState(false);
  const [addressError, setAddressError] = useState(false);
  const [modelIdError, setModelIdError] = useState(false);
  const [channelIdError, setChannelIdError] = useState(false);
  const [intervalError, setIntervalError] = useState(false);

  const [dataValue, handleFieldChange, setData] = useForm(() => ({
    name: '',
    address: 0,
    modelId: 0,
    channelId: 0,
    interval: 300,
    order: 0,
    logicDevice: false,
    ...device,
    deviceIds: allDeviceIdsExisted ? device.deviceIds || [] : [],
  }));

  const nameField = useRef(null);

  const handleSubmit = useCallback(() => {
    const cleanData = {
      ...dataValue,
      name: dataValue.name.trim(),
    };

    let error;
    if (!cleanData.name) {
      setNameError(true);
      error = true;
    }
    if (cleanData.address < 0) {
      setAddressError(true);
      error = true;
    }
    if (!cleanData.modelId) {
      setModelIdError(true);
      error = true;
    }
    if (!cleanData.channelId) {
      setChannelIdError(true);
      error = true;
    }
    if (!cleanData.interval) {
      setIntervalError(true);
      error = true;
    }
    if (error) return;

    if (cleanData !== device) {
      onUpdate(cleanData);
    }
    onClose();
  }, [onUpdate, onClose, device, dataValue]);

  useEffect(() => {
    nameField.current.select();
  }, []);

  const handleChangeLogic = useCallback(
    (e, data) => {
      setData({
        ...dataValue,
        logicDevice: data.checked,
      });
    },
    [dataValue, setData],
  );
  return (
    <>
      <Modal.Header>
        {t('common.editDevice', {
          context: 'title',
        })}
      </Modal.Header>
      <Modal.Content>
        <Form onSubmit={handleSubmit}>
          <Form.Field required error={nameError} onFocus={() => setNameError(false)}>
            <label htmlFor="name">{t('common.enterDeviceTitle')}</label>
            <Input
              fluid
              ref={nameField}
              name="name"
              value={dataValue.name}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={addressError} onFocus={() => setAddressError(false)}>
            <label htmlFor="address">{t('common.setDeviceAddress')}</label>
            <Input
              fluid
              type="number"
              name="address"
              value={dataValue.address}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={modelIdError} onFocus={() => setModelIdError(false)}>
            <label htmlFor="modelId">{t('common.selectDeviceModel')}</label>
            <Dropdown
              name="modelId"
              className={styles.field}
              placeholder={t('common.selectDeviceModel')}
              search
              fluid
              selection
              value={dataValue.modelId}
              options={modelsOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={channelIdError} onFocus={() => setChannelIdError(false)}>
            <label htmlFor="channelId">{t('common.selectDeviceChanel')}</label>
            <Dropdown
              name="channelId"
              className={styles.field}
              placeholder={t('common.selectDeviceChanel')}
              search
              fluid
              selection
              value={dataValue.channelId}
              options={channelsOptions}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field required error={intervalError} onFocus={() => setIntervalError(false)}>
            <label htmlFor="convertion">{t('common.selectInterval')}</label>
            <Input
              fluid
              type="number"
              name="interval"
              value={dataValue.interval}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field>
            <label htmlFor="order">{t('common.enterItemPosition')}</label>
            <Input
              fluid
              type="number"
              name="order"
              value={dataValue.order || 0}
              className={styles.field}
              onChange={handleFieldChange}
            />
          </Form.Field>
          <Form.Field>
            <Checkbox
              name="logicDevice"
              fitted
              checked={dataValue.logicDevice}
              onChange={handleChangeLogic}
              label={t('common.isDeviceVirtual')}
            />
          </Form.Field>
          {dataValue.logicDevice && (
            <Form.Field>
              <label htmlFor="deviceIds">{t('common.components.devices')}</label>
              <Form.Select
                name="deviceIds"
                className={styles.field}
                placeholder={t('common.selectDevice')}
                search
                fluid
                selection
                multiple
                value={dataValue.deviceIds}
                options={devicesOptions}
                onChange={handleFieldChange}
              />
            </Form.Field>
          )}
          <Button color="green" icon="checkmark" content={t('action.save')} floated="right" />
          <div className="clearBoth" />
        </Form>
      </Modal.Content>
    </>
  );
});

DeviceEditStep.propTypes = {
  devices: PropTypes.arrayOf(PropTypes.any).isRequired,
  channels: PropTypes.arrayOf(PropTypes.any).isRequired,
  models: PropTypes.arrayOf(PropTypes.any).isRequired,
  device: PropTypes.objectOf(PropTypes.any).isRequired,
  onUpdate: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default DeviceEditStep;
